import { Component } from "@mzw/component";
import { isLowPowerDevice, prefersReducedMotion, supportsViewTransitions } from "@mzw/utilities";

const TRANSITION_NAME = "zoom-dialog-image";
const IMAGE_LOAD_TIMEOUT = 10_000;

/**
 * Fallback for browsers without View Transitions API
 * @param {() => void} callback
 * @returns {{ finished: Promise<void> }}
 */
const transition = (callback) => {
	if (!supportsViewTransitions() || isLowPowerDevice() || prefersReducedMotion()) {
		callback();
		return { finished: Promise.resolve() };
	}
	return document.startViewTransition(callback);
};

/**
 * Helper to set/clear view transition name on an element
 * @param {HTMLElement | null | undefined} element
 * @param {boolean} active
 */
const setTransitionName = (element, active) => {
	if (!element || prefersReducedMotion()) return;
	element.style.viewTransitionName = active ? TRANSITION_NAME : "";
};

/**
 * Load an image with timeout and error handling
 * @param {string} src
 * @returns {Promise<HTMLImageElement>}
 */
const loadImage = (src) => {
	return new Promise((resolve, reject) => {
		const image = new Image();
		image.className = "high-res";
		image.src = src;
		image.loading = "eager";
		image.fetchPriority = "high";
		image.alt = "";

		const timeout = setTimeout(() => reject(new Error("Image load timeout")), IMAGE_LOAD_TIMEOUT);

		image.addEventListener(
			"load",
			() => {
				clearTimeout(timeout);
				resolve(image);
			},
			{ once: true }
		);

		image.addEventListener(
			"error",
			() => {
				clearTimeout(timeout);
				reject(new Error("Failed to load image"));
			},
			{ once: true }
		);
	});
};

/**
 * A dialog component for zooming into images with view transition support.
 * @extends {Component}
 */
export class ZoomDialogComponent extends Component {
	requiredRefs = ["dialog"];

	/** @type {HTMLElement | null} */
	#sourceElement = null;

	static tagName = "zoom-dialog";

	/**
	 * Registers the custom element
	 * @param {string} [tagName]
	 * @param {CustomElementRegistry} [registry]
	 */
	static register(tagName = ZoomDialogComponent.tagName, registry = globalThis.customElements) {
		if (!registry?.get(tagName)) {
			registry?.define(tagName || ZoomDialogComponent.tagName, ZoomDialogComponent);
		}
	}

	/** @returns {HTMLImageElement | null} */
	get dialogImage() {
		return this.refs.dialog?.querySelector("img");
	}

	connectedCallback() {
		super.connectedCallback();
		this.refs.dialog?.addEventListener("click", this.#handleDialogClick);
		this.refs.dialog?.addEventListener("cancel", this.#handleCancel);
	}

	disconnectedCallback() {
		super.disconnectedCallback?.();
		this.refs.dialog?.removeEventListener("click", this.#handleDialogClick);
		this.refs.dialog?.removeEventListener("cancel", this.#handleCancel);
	}

	/**
	 * Handle click on the dialog backdrop to close
	 * @param {MouseEvent} event
	 */
	#handleDialogClick = (event) => {
		if (event.currentTarget === this.refs.dialog) {
			this.close();
		}
	};

	/**
	 * Intercept cancel event (from Escape key or closedby="any") to run transition first
	 * @param {Event} event
	 */
	#handleCancel = (event) => {
		event.preventDefault();
		this.close();
	};

	/** @type {Map<string, Promise<HTMLImageElement>>} */
	#prefetchCache = new Map();

	/**
	 * Prefetches the high-resolution image on hover for faster loading
	 * @param {MouseEvent} event
	 */
	prefetch(event) {
		const sourceElement = event.target?.parentElement?.querySelector("[data-max-resolution]");
		const src = sourceElement?.dataset?.maxResolution;

		if (!src || this.#prefetchCache.has(src)) return;

		this.#prefetchCache.set(src, loadImage(src));
	}

	/**
	 * Opens the zoom dialog with the high-resolution image
	 * @param {MouseEvent} event
	 */
	async open(event) {
		const { dialog } = this.refs;
		const target = /** @type {HTMLElement} */ (event.target);

		this.#sourceElement = target.parentElement?.querySelector("[data-max-resolution]") ?? null;

		if (!this.#sourceElement?.dataset?.maxResolution) {
			console.warn("No high-res image available");
			return;
		}

		target.classList.add("loading");

		try {
			const src = this.#sourceElement.dataset.maxResolution;
			const imagePromise = this.#prefetchCache.get(src) ?? loadImage(src);
			const image = await imagePromise;
			this.dialogImage?.remove();
			dialog.append(image);
		} catch (error) {
			console.error("Failed to load high-res image:", error);
			target.classList.remove("loading");
			return;
		}

		target.classList.remove("loading");

		// Calculate scroll position based on click coordinates
		const rect = target.getBoundingClientRect();
		const xPercent = ((event.clientX - rect.left) / rect.width) * 100;
		const yPercent = ((event.clientY - rect.top) / rect.height) * 100;

		// Set transition name on source element before transition
		setTransitionName(this.#sourceElement, true);

		transition(() => {
			// Move transition name from source to dialog image
			setTransitionName(this.#sourceElement, false);
			setTransitionName(this.dialogImage, true);

			dialog.showModal();

			// Scroll to the clicked position
			dialog.scrollTo({
				left: (dialog.scrollWidth * xPercent) / 100 - dialog.clientWidth / 2,
				top: (dialog.scrollHeight * yPercent) / 100 - dialog.clientHeight / 2,
			});
		}).finished.then(() => {
			// Clean up transition name after animation completes
			setTransitionName(this.dialogImage, false);
		});
	}

	/**
	 * Closes the zoom dialog with a view transition
	 */
	close() {
		const { dialog } = this.refs;

		// Set transition name on dialog image before transition
		setTransitionName(this.dialogImage, true);

		transition(() => {
			// Move transition name from dialog image to source
			setTransitionName(this.dialogImage, false);
			setTransitionName(this.#sourceElement, true);

			dialog.close();
		}).finished.then(() => {
			// Clean up transition name after animation completes
			setTransitionName(this.#sourceElement, false);
			this.#sourceElement = null;
		});
	}
}

ZoomDialogComponent.register();
